import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import {
  User as FirebaseUser,
  onAuthStateChanged,
  getRedirectResult,
} from "firebase/auth";
import { auth } from "../services/firebase";
import {
  signInWithGoogle,
  signOut as authSignOut,
  syncUserProfile,
  subscribeToUserProfile,
  isAllowedDomain,
} from "../services/auth";
import { UserProfile } from "../types";

export interface AuthContextType {
  user: UserProfile | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  error: string | null;
  signIn: (preferRedirect?: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export interface AuthProviderProps {
  children: ReactNode;
  initialUser?: UserProfile | null;
  initialLoading?: boolean;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  initialUser = null,
  initialLoading = true,
}) => {
  const [user, setUser] = useState<UserProfile | null>(initialUser);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState<boolean>(initialLoading);
  const [error, setError] = useState<string | null>(null);

  const refreshProfile = useCallback(async () => {
    if (!auth.currentUser) {
      setUser(null);
      return;
    }
    try {
      const profile = await syncUserProfile(auth.currentUser);
      setUser(profile);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to sync profile";
      setError(message);
    }
  }, []);

  const signIn = useCallback(async (preferRedirect = false) => {
    setError(null);
    setLoading(true);
    try {
      const profile = await signInWithGoogle(preferRedirect);
      if (profile) {
        setUser(profile);
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Authentication failed";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    setError(null);
    try {
      await authSignOut();
      setUser(null);
      setFirebaseUser(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to sign out";
      setError(message);
      throw err;
    }
  }, []);

  useEffect(() => {
    let unsubscribeProfile: (() => void) | null = null;

    if (import.meta.env.MODE !== "test") {
      getRedirectResult(auth)
        .then(async (result) => {
          if (result && result.user) {
            if (!isAllowedDomain(result.user.email)) {
              setError("Only @mlconnections.com accounts are permitted.");
              await authSignOut();
              return;
            }
            const profile = await syncUserProfile(result.user);
            setUser(profile);
          }
        })
        .catch((err: unknown) => {
          console.warn("getRedirectResult notice:", err);
        });
    }

    const unsubscribeAuth = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);

      if (unsubscribeProfile) {
        unsubscribeProfile();
        unsubscribeProfile = null;
      }

      if (!fbUser) {
        if (import.meta.env.MODE !== "test" || !initialUser) {
          setUser(null);
        }
        setLoading(false);
        return;
      }

      if (!isAllowedDomain(fbUser.email)) {
        setError("Only @mlconnections.com accounts are permitted.");
        await authSignOut();
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        // Initial sync
        const profile = await syncUserProfile(fbUser);
        setUser(profile);

        // Realtime subscription for admin approval changes
        unsubscribeProfile = subscribeToUserProfile(
          fbUser.uid,
          (updatedProfile) => {
            if (updatedProfile) {
              setUser(updatedProfile);
            }
          },
          (err) => {
            console.error("User profile subscription error:", err);
          },
        );
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Failed to initialize profile";
        setError(message);
        setUser(null);
      } finally {
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeProfile) {
        unsubscribeProfile();
      }
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        firebaseUser,
        loading,
        error,
        signIn,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
